import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './LoginDialog.css';
import { authAPI } from './services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { enUS, he, bg } from 'date-fns/locale';
import ReCAPTCHA from 'react-google-recaptcha';

const DATE_FNS_LOCALE = { en: enUS, he, bg };


const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LoginDialog({ onLogin, onClose }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'

  // Login fields
  const [loginUsername, setLoginUsername] = useState(() => localStorage.getItem('rememberedUsername') || '');
  const [loginPassword, setLoginPassword] = useState(() => localStorage.getItem('rememberedPassword') || '');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedUsername'));
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Forgot password fields
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Register fields
  const [regUsername, setRegUsername] = useState('');
  const [regDisplayName, setRegDisplayName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regBirthDate, setRegBirthDate] = useState(null);
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const [regTermsAccepted, setRegTermsAccepted] = useState(false);

  // Location picker
  const [regLocation, setRegLocation] = useState(null); // { lat, lng }
  const [regLocationAddress, setRegLocationAddress] = useState('');
  const [regCountry, setRegCountry] = useState('');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const regMapRef = useRef(null);
  const regMapInstanceRef = useRef(null);
  const regMarkerRef = useRef(null);

  const reverseGeocodeReg = (lat, lng) => {
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setRegLocationAddress(results[0].formatted_address);
        const countryComponent = results[0].address_components?.find(c => c.types.includes('country'));
        setRegCountry(countryComponent?.short_name || '');
      } else {
        setRegLocationAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        setRegCountry('');
      }
    });
  };

  useEffect(() => {
    if (!showMapPicker) return;

    const initMap = () => {
      if (!regMapRef.current || !window.google?.maps) {
        setTimeout(initMap, 100);
        return;
      }

      const defaultLat = regLocation?.lat ?? 42.6977;
      const defaultLng = regLocation?.lng ?? 23.3219;

      const map = new window.google.maps.Map(regMapRef.current, {
        center: { lat: defaultLat, lng: defaultLng },
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
      });

      const marker = new window.google.maps.Marker({
        position: { lat: defaultLat, lng: defaultLng },
        map,
        draggable: true,
      });

      if (!regLocation) {
        setRegLocation({ lat: defaultLat, lng: defaultLng });
        reverseGeocodeReg(defaultLat, defaultLng);
      }

      marker.addListener('dragend', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setRegLocation({ lat, lng });
        reverseGeocodeReg(lat, lng);
      });

      map.addListener('click', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        marker.setPosition(e.latLng);
        setRegLocation({ lat, lng });
        reverseGeocodeReg(lat, lng);
      });

      regMapInstanceRef.current = map;
      regMarkerRef.current = marker;
    };

    setTimeout(initMap, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMapPicker]);

  const handleMapDone = () => {
    setShowMapPicker(false);
    regMapInstanceRef.current = null;
    regMarkerRef.current = null;
  };

  const handleMapCancel = () => {
    setRegLocation(null);
    setRegLocationAddress('');
    setShowMapPicker(false);
    regMapInstanceRef.current = null;
    regMarkerRef.current = null;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotError('');
    try {
      await authAPI.forgotPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch {
      setForgotError(t('loginDialog.resetPasswordFailed'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginUsername || !loginPassword) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await authAPI.login(loginUsername.trim(), loginPassword);
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', loginUsername.trim());
        localStorage.setItem('rememberedPassword', loginPassword);
      } else {
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
      }
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      onLogin(response.user);
    } catch (err) {
      const msg = err.response?.data || '';
      setLoginError(typeof msg === 'string' && msg ? msg : t('loginDialog.invalidCredentials'));
    } finally {
      setLoginLoading(false);
    }
  };

  const maxBirthDate = new Date();
  maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 16);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regUsername || !regDisplayName || !regEmail || !regBirthDate || !regPassword || !regConfirm || !regLocation || !captchaToken) return;
    if (!EMAIL_REGEX.test(regEmail.trim())) {
      setRegError(t('loginDialog.invalidEmail'));
      return;
    }
    if (regBirthDate > maxBirthDate) {
      setRegError(t('loginDialog.ageTooYoung'));
      return;
    }
    if (regPassword.length < 6) {
      setRegError(t('loginDialog.passwordTooShort'));
      return;
    }
    if (regPassword !== regConfirm) {
      setRegError(t('loginDialog.passwordsMismatch'));
      return;
    }
    setRegLoading(true);
    setRegError('');
    try {
      const response = await authAPI.register({
        username: regUsername.trim(),
        displayName: regDisplayName.trim(),
        email: regEmail.trim(),
        birthDate: regBirthDate ? regBirthDate.toISOString() : null,
        password: regPassword,
        homeLatitude: regLocation?.lat ?? null,
        homeLongitude: regLocation?.lng ?? null,
        country: regCountry || null,
        language: i18n.language,
        captchaToken,
      });
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      onLogin(response.user);
    } catch (err) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
      const msg = err.response?.data || '';
      setRegError(typeof msg === 'string' && msg ? msg : t('loginDialog.registrationFailed'));
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <>
      {showMapPicker && createPortal(
        <div className="login-map-backdrop">
          <div className="login-map-overlay">
            <div className="login-map-toolbar">
              <span className="login-map-title">{t('loginDialog.chooseLocation')}</span>
              <div className="login-map-toolbar-actions">
                <button type="button" className="login-map-cancel-btn" onClick={handleMapCancel}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="login-map-done-btn" onClick={handleMapDone}>
                  {t('common.done')}
                </button>
              </div>
            </div>
            {regLocationAddress && (
              <div className="login-map-address-bar">📍 {regLocationAddress}</div>
            )}
            <div className="login-map-hint">{t('loginDialog.locationHint')}</div>
            <div ref={regMapRef} className="login-map-container" />
          </div>
        </div>,
        document.body
      )}

      <div className="login-overlay">


      <div className="login-dialog-wide">

        {onClose && (
          <button className="login-close-btn" onClick={onClose} aria-label="Close">✕</button>
        )}

        {/* ── Left: vision panel ─────────────────────────── */}
        <div className="login-vision-panel">
          <div className="login-vision-logo">🌵</div>
          <h1 className="login-vision-title">Cactus #norms</h1>
          <div className="login-vision-divider" />
          {t('loginDialog.visionParagraphs', { returnObjects: true }).map((p, i) => (
            <p key={i} className="login-vision-text">{p}</p>
          ))}
          <div className="login-age-badge">16+</div>
        </div>

        {/* ── Right: auth panel ──────────────────────────── */}
        <div className="login-auth-panel">

          <div className="login-lang-row">
            <select
              className="login-lang-select"
              value={i18n.language}
              onChange={e => i18n.changeLanguage(e.target.value)}
            >
              <option value="en">EN</option>
              <option value="he">עב</option>
              <option value="bg">БГ</option>
            </select>
          </div>

          {mode === 'login' ? (
            <>
              <form onSubmit={handleLogin} className="login-form">
                <label className="login-label">{t('loginDialog.username')}</label>
                <input
                  className="login-input"
                  type="text"
                  value={loginUsername}
                  onChange={e => setLoginUsername(e.target.value)}
                  placeholder={t('loginDialog.usernamePlaceholder')}
                  autoFocus
                  autoComplete="username"
                />

                <label className="login-label">{t('loginDialog.password')}</label>
                <input
                  className="login-input"
                  type="password"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />

                {loginError && <p className="login-error">{loginError}</p>}

                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  {t('loginDialog.rememberMe')}
                </label>

                <button
                  className="login-submit-btn"
                  type="submit"
                  disabled={loginLoading || !loginUsername || !loginPassword}
                >
                  {loginLoading ? t('loginDialog.signingIn') : t('loginDialog.signIn')}
                </button>
              </form>

              <p className="login-switch-text">
                {t('loginDialog.noAccount')}{' '}
                <button className="login-switch-link" onClick={() => { setMode('register'); setLoginError(''); }}>
                  {t('loginDialog.createOne')}
                </button>
              </p>
              <p className="login-switch-text">
                <button className="login-switch-link" onClick={() => { setMode('forgot'); setLoginError(''); }}>
                  {t('loginDialog.forgotPassword')}
                </button>
              </p>
            </>
          ) : mode === 'forgot' ? (
            <>
              <h2 className="login-auth-title">{t('loginDialog.forgotTitle')}</h2>
              <p className="login-auth-sub">{t('loginDialog.forgotSub')}</p>

              {!forgotSent ? (
                <form onSubmit={handleForgotPassword} className="login-form">
                  <label className="login-label">{t('loginDialog.email')}</label>
                  <input
                    className="login-input"
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder={t('loginDialog.emailPlaceholder')}
                    autoFocus
                    autoComplete="email"
                  />
                  {forgotError && <p className="login-error">{forgotError}</p>}
                  <button
                    className="login-submit-btn"
                    type="submit"
                    disabled={forgotLoading || !forgotEmail}
                  >
                    {forgotLoading ? t('loginDialog.sendingResetLink') : t('loginDialog.sendResetLink')}
                  </button>
                </form>
              ) : (
                <p className="login-auth-sub" style={{ color: '#4CAF50', fontWeight: 500 }}>
                  {t('loginDialog.resetEmailSent')}
                </p>
              )}

              <p className="login-switch-text">
                <button className="login-switch-link" onClick={() => { setMode('login'); setForgotEmail(''); setForgotError(''); setForgotSent(false); }}>
                  {t('loginDialog.backToLogin')}
                </button>
              </p>
            </>
          ) : (
            <>
              <h2 className="login-auth-title">{t('loginDialog.joinTitle')}</h2>
              <p className="login-auth-sub">{t('loginDialog.createYourAccount')}</p>

              <form onSubmit={handleRegister} className="login-form">
                <label className="login-label">{t('loginDialog.username')}</label>
                <input
                  className="login-input"
                  type="text"
                  value={regUsername}
                  onChange={e => setRegUsername(e.target.value)}
                  placeholder={t('loginDialog.usernamePlaceholder')}
                  autoFocus
                  autoComplete="username"
                />

                <label className="login-label">{t('loginDialog.displayName')}</label>
                <input
                  className="login-input"
                  type="text"
                  value={regDisplayName}
                  onChange={e => setRegDisplayName(e.target.value)}
                  placeholder={t('loginDialog.displayNamePlaceholder')}
                  autoComplete="name"
                />

                <label className="login-label">{t('loginDialog.email')}</label>
                <input
                  className="login-input"
                  type="email"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  placeholder={t('loginDialog.emailPlaceholder')}
                  autoComplete="email"
                />

                <label className="login-label">{t('loginDialog.birthDate')} <span className="login-age-note">({t('loginDialog.mustBe16')})</span></label>
                <DatePicker
                  selected={regBirthDate}
                  onChange={(date) => setRegBirthDate(date)}
                  dateFormat="d MMMM yyyy"
                  locale={DATE_FNS_LOCALE[i18n.language] ?? enUS}
                  maxDate={maxBirthDate}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  placeholderText={t('loginDialog.birthDate')}
                  className="login-input"
                  wrapperClassName="login-datepicker-wrapper"
                  onChangeRaw={(e) => e.preventDefault()}
                  popperPlacement="top-start"
                />

                <label className="login-label">{t('loginDialog.preferredLocation')}</label>
                <button
                  type="button"
                  className="login-location-btn"
                  onClick={() => setShowMapPicker(true)}
                >
                  📍 {regLocationAddress ? t('loginDialog.changeLocation') : t('loginDialog.setLocation')}
                </button>
                {regLocationAddress && (
                  <span className="login-location-chip">📍 {regLocationAddress}</span>
                )}

                <label className="login-label">{t('loginDialog.password')}</label>
                <input
                  className="login-input"
                  type="password"
                  value={regPassword}
                  onChange={e => setRegPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />

                <label className="login-label">{t('loginDialog.confirmPassword')}</label>
                <input
                  className="login-input"
                  type="password"
                  value={regConfirm}
                  onChange={e => setRegConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />

                {regError && <p className="login-error">{regError}</p>}

                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={regTermsAccepted}
                    onChange={e => setRegTermsAccepted(e.target.checked)}
                  />
                  {t('loginDialog.agreeToTerms')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    {t('loginDialog.termsOfUse')}
                  </a>
                </label>

                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                  onExpired={() => setCaptchaToken(null)}
                />

                <button
                  className="login-submit-btn"
                  type="submit"
                  disabled={regLoading || !regUsername || !regDisplayName || !regEmail || !regBirthDate || !regPassword || !regConfirm || !regLocation || !regTermsAccepted || !captchaToken}
                >
                  {regLoading ? t('loginDialog.creatingAccount') : t('loginDialog.createAccount')}
                </button>
              </form>

              <p className="login-switch-text">
                {t('loginDialog.alreadyHaveAccount')}{' '}
                <button className="login-switch-link" onClick={() => { setMode('login'); setRegError(''); }}>
                  {t('loginDialog.signIn')}
                </button>
              </p>
            </>
          )}
        </div>

      </div>
    </div>
    </>
  );
}

export default LoginDialog;